<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const selectedChannel = ref(null);
const selectedGroups = ref([]);
const wahaChannels = ref([]);
const wahaGroups = ref([]);
const isLoadingChannels = ref(false);
const isLoadingGroups = ref(false);
const isSyncing = ref(false);

const hasSelectedGroups = computed(() => selectedGroups.value.length > 0);
const canProceed = computed(
  () => selectedChannel.value && hasSelectedGroups.value
);

const fetchWahaChannels = async () => {
  isLoadingChannels.value = true;
  try {
    const response = await store.dispatch('contacts/fetchWahaChannels');
    wahaChannels.value = response.data.channels || [];
  } catch (error) {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.ERROR_FETCHING_CHANNELS')
    );
  } finally {
    isLoadingChannels.value = false;
  }
};

const fetchWahaGroups = async () => {
  if (!selectedChannel.value) return;

  isLoadingGroups.value = true;
  wahaGroups.value = [];
  selectedGroups.value = [];

  try {
    const response = await store.dispatch(
      'contacts/fetchWahaGroups',
      selectedChannel.value
    );
    wahaGroups.value = response.data.groups || [];
  } catch (error) {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.ERROR_FETCHING_GROUPS')
    );
  } finally {
    isLoadingGroups.value = false;
  }
};

watch(selectedChannel, () => {
  if (selectedChannel.value) {
    fetchWahaGroups();
  } else {
    wahaGroups.value = [];
    selectedGroups.value = [];
  }
});

const toggleGroup = groupId => {
  const index = selectedGroups.value.findIndex(g => g.id === groupId);
  if (index > -1) {
    selectedGroups.value.splice(index, 1);
  } else {
    const group = wahaGroups.value.find(g => g.id === groupId);
    if (group) {
      selectedGroups.value.push(group);
    }
  }
};

const isGroupSelected = groupId => {
  return selectedGroups.value.some(g => g.id === groupId);
};

const handleSync = async () => {
  if (!hasSelectedGroups.value) return;

  isSyncing.value = true;
  try {
    await store.dispatch('contacts/syncWahaGroups', selectedGroups.value);
    useAlert(t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.SUCCESS_MESSAGE'));
    dialogRef.value?.close();
    selectedGroups.value = [];
  } catch (error) {
    useAlert(
      error.message ||
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.ERROR_MESSAGE')
    );
  } finally {
    isSyncing.value = false;
  }
};

const onDialogOpen = () => {
  selectedChannel.value = null;
  selectedGroups.value = [];
  wahaGroups.value = [];
  fetchWahaChannels();
};

const onDialogClose = () => {
  selectedChannel.value = null;
  selectedGroups.value = [];
  wahaGroups.value = [];
  wahaChannels.value = [];
};

defineExpose({ dialogRef, onDialogOpen });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.TITLE')"
    :confirm-button-label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.SYNC')"
    :is-loading="isSyncing"
    :disable-confirm-button="!canProceed || isSyncing"
    @confirm="handleSync"
    @open="onDialogOpen"
    @close="onDialogClose"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.DESCRIPTION') }}
      </p>
    </template>

    <div class="flex flex-col gap-4">
      <!-- Step 1: Select Channel -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.SELECT_CHANNEL') }}
        </label>

        <div
          v-if="isLoadingChannels"
          class="flex items-center justify-center py-8"
        >
          <Spinner />
        </div>

        <div v-else-if="wahaChannels.length === 0" class="py-4 text-center">
          <p class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.NO_CHANNELS') }}
          </p>
        </div>

        <div v-else class="flex flex-col gap-2">
          <div
            v-for="channel in wahaChannels"
            :key="channel.id"
            class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-n-alpha-2 border-n-subtle"
            :class="{
              'bg-n-alpha-2 border-n-brand': selectedChannel === channel.id,
            }"
            @click="selectedChannel = channel.id"
          >
            <div
              class="flex items-center justify-center w-5 h-5 border-2 rounded-full flex-shrink-0"
              :class="
                selectedChannel === channel.id
                  ? 'bg-n-brand border-n-brand'
                  : 'border-n-subtle'
              "
            >
              <div
                v-if="selectedChannel === channel.id"
                class="w-2 h-2 rounded-full bg-white"
              />
            </div>

            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ channel.name }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Select Groups (only shown after channel selection) -->
      <div v-if="selectedChannel" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.SELECT_GROUPS') }}
          <span class="text-n-slate-11">({{ selectedGroups.length }})</span>
        </label>

        <div
          v-if="isLoadingGroups"
          class="flex items-center justify-center py-8"
        >
          <Spinner />
        </div>

        <div v-else-if="wahaGroups.length === 0" class="py-4 text-center">
          <p class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.SYNC_WAHA.NO_GROUPS') }}
          </p>
        </div>

        <div v-else class="flex flex-col gap-2 max-h-96 overflow-y-auto">
          <div
            v-for="group in wahaGroups"
            :key="group.id"
            class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-n-alpha-2 border-n-subtle"
            :class="{
              'bg-n-alpha-2 border-n-brand': isGroupSelected(group.id),
            }"
            @click="toggleGroup(group.id)"
          >
            <div
              class="flex items-center justify-center w-5 h-5 border-2 rounded flex-shrink-0"
              :class="
                isGroupSelected(group.id)
                  ? 'bg-n-brand border-n-brand'
                  : 'border-n-subtle'
              "
            >
              <span
                v-if="isGroupSelected(group.id)"
                class="i-lucide-check text-white size-3"
              />
            </div>

            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ group.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
